import { Dispatch } from 'redux';
import {
  decodeToken,
  forgotPasswordSent,
  getCustomerId,
  getTokenInfo,
  getUserId,
  logoutAndDeleteToken,
  setAuthTokenOnLogin,
  setAuthTokenOnRefresh,
  setMfaToken,
  StoredToken,
  TokenResponse,
} from './authReducer';
import { LoginFormValues } from './Login';
import { AppState } from '../store/store';
import tracker from '../tracking/tracker';
import Tracker from '../tracking/tracker';
import { ResetPasswordFormValues } from './ResetPassword';
import { showFeedback } from '../feedback/feedbackReducer';
import { ForgotPasswordFormValues } from './ForgotPassword';
import { userApi, userForgotPasswordApi } from '../apis';
import { LocationState } from '../route/PrivateRoute';
import { CLIENT_AUTHORIZATION } from './constants';
import { FormikHelpers } from 'formik/dist/types';
import { useLocation, useNavigate } from 'react-router';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OrganisationChooseModalValues } from '../organisation/choose/OrganisationChooseModal';
import { TurnstileInstance } from '@marsidev/react-turnstile';
import { useQueryClient } from '@tanstack/react-query';
import { parseErrorMessage } from '../common/errors/helpers';
import { InternalNavigation } from '../navigation/helper';
import ErrorIgnoreSentry from '../tracking/ErrorIgnoreSentry';

const convertToken = (json: any) => {
  return {
    accessToken: json.access_token,
    expiresIn: json.expires_in,
    refreshToken: json.refresh_token,
    scope: json.scope,
    tokenType: json.token_type,
    error: json.error,
    errorDescription: json.error_description,
    mfaToken: json.mfa_token,
  };
};

export async function loginWithPassword(
  username: string,
  password: string,
  cfTurnstileResponse: string | undefined
): Promise<TokenResponse> {
  const formData = new FormData();
  formData.append('grant_type', 'password');
  formData.append('password', password);
  formData.append('username', username);
  if (cfTurnstileResponse !== undefined) {
    formData.append('cfTurnstileResponse', cfTurnstileResponse);
  }
  const request: any = {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: CLIENT_AUTHORIZATION,
    },
  };
  return await fetch(`/oauth/token${QUERY_PARAMS_FOR_LOGGING.password}`, request)
    .then(convertResponseToJson)
    .then(convertToken);
}

export async function convertResponseToJson(response: Response): Promise<any> {
  const body = await response.text();
  if (response.status === 429) {
    throw new ErrorIgnoreSentry(response.status, 'You are being rate limited. Try again shortly');
  }
  if (response.status === 401 || response.status === 400) {
    throw new ErrorIgnoreSentry(response.status, 'Credentials are invalid or account is locked.');
  }
  if (response.status < 200 || response.status >= 300) {
    Tracker.breadcrumb(body);
    Tracker.error(`Unexpected /oauth/token response status: ${response.status}`);
    throw new Error('We are experiencing problems. Try again shortly.');
  }
  try {
    return await JSON.parse(body);
  } catch (e: unknown) {
    Tracker.breadcrumb(body);
    Tracker.error(e);
    throw new Error('We are experiencing problems. Try again shortly.');
  }
}

export async function loginWith2fa(otpCode: string, otpToken: string | null): Promise<TokenResponse> {
  const formData = new FormData();
  formData.append('grant_type', 'mfa');
  const request: any = {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: CLIENT_AUTHORIZATION,
    },
  };

  request.headers['X-OTP-CODE-SALV'] = otpCode;
  request.headers['X-OTP-TOKEN-SALV'] = otpToken;

  return await fetch(`/oauth/token${QUERY_PARAMS_FOR_LOGGING.mfa}`, request)
    .then(convertResponseToJson)
    .then(convertToken);
}

async function loginWithToken(organisationId: number | undefined, token: string | null): Promise<TokenResponse> {
  const formData = new FormData();
  formData.append('grant_type', 'implicit');
  if (token) {
    formData.append('access_token', token);
  }
  if (organisationId) {
    formData.append('organisation_id', organisationId.toString());
  }
  const request: any = {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: CLIENT_AUTHORIZATION,
    },
  };
  return await fetch(`/oauth/token${QUERY_PARAMS_FOR_LOGGING.implicit}`, request)
    .then(convertResponseToJson)
    .then(convertToken);
}

export function useRemoveNavigationState() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    navigate(location, { state: null });
  }, [navigate, location]);
}

export function useSwitchOrganisationForm() {
  const dispatch = useDispatch();
  const currentToken = useSelector((state: AppState) => state.auth.tokenResponse?.accessToken || null);
  const queryClient = useQueryClient();
  const removeNavigationState = useRemoveNavigationState();
  const [isLoading, setLoading] = useState(false);

  const switchOrganisation = useCallback(
    async (
      values: OrganisationChooseModalValues | null,
      helpers: FormikHelpers<OrganisationChooseModalValues> | null
    ) => {
      const newOrgId = values?.organisation?.value ? Number(values.organisation.value) : undefined;
      try {
        const newToken = await loginWithToken(newOrgId, currentToken);
        await postSuccessfulLogin(dispatch, newToken, null);
        queryClient.removeQueries();
        removeNavigationState();
      } catch (e: any) {
        helpers?.setStatus(await parseErrorMessage(e));
      } finally {
        helpers?.setSubmitting(false);
      }
    },
    [currentToken, removeNavigationState]
  );

  const switchOrganisationV2 = useCallback(
    async (organisationId: number | undefined) => {
      setLoading(true);
      const newToken = await loginWithToken(organisationId, currentToken);
      await postSuccessfulLogin(dispatch, newToken, null);
      queryClient.removeQueries();
      setLoading(false);
    },
    [currentToken]
  );

  return {
    switchOrganisation,
    switchOrganisationV2,
    isLoading,
  };
}

export function useSubmitLoginForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const turnstileRef = React.useRef<TurnstileInstance | undefined>(undefined);

  const submitLoginForm = useCallback(
    async (values: LoginFormValues, { setStatus, setSubmitting }: FormikHelpers<LoginFormValues>) => {
      try {
        const accessToken = await loginWithPassword(
          values.username,
          values.password,
          turnstileRef.current?.getResponse()
        );

        if (accessToken.error) {
          setStatus(accessToken.errorDescription);
          return;
        }

        if (!accessToken.refreshToken && accessToken.accessToken) {
          dispatch(setMfaToken(accessToken.accessToken));
          navigate('/two-factor', { state: location.state, replace: true });
          return;
        }
        await postSuccessfulLogin(dispatch, accessToken, location);
        navigate(InternalNavigation.root, { state: location.state, replace: true });
      } catch (e: any) {
        setStatus(await parseErrorMessage(e));
      } finally {
        setSubmitting(false);
      }
    },
    []
  );

  return {
    submitLoginForm,
    turnstileRef,
  };
}

const identifyUser = () => async (dispatch: Dispatch, getState: () => AppState) => {
  const userId = getUserId(getState());
  const customerId = getCustomerId(getState());
  if (userId != null) {
    tracker.setUser(userId, customerId);
  }
};

export async function postSuccessfulLogin(
  dispatch: Dispatch<any>,
  accessToken: TokenResponse,
  location: LocationState | null
) {
  const tokenInfo = getTokenInfo(decodeToken(accessToken.accessToken));
  if (tokenInfo?.isAdmin && location?.state?.organisationId) {
    const orgToken = await loginWithToken(location.state.organisationId, accessToken.accessToken);
    dispatch(setAuthTokenOnLogin(orgToken));
  } else {
    dispatch(setAuthTokenOnLogin(accessToken));
  }
  dispatch(identifyUser());
}

export function useSubmitResetPasswordForm() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const submitResetPasswordForm = useCallback(
    async (values: ResetPasswordFormValues, { setStatus, setSubmitting }: FormikHelpers<ResetPasswordFormValues>) => {
      try {
        setStatus({ message: '' });
        setSubmitting(true);
        await userForgotPasswordApi.resetPassword({
          internalApiResetPasswordForm: {
            password: values.password,
            token: values.token || '',
          },
        });

        setStatus({
          message: 'Password has been reset. You will be redirected to login.',
          success: true,
        });
        await new Promise((resolve) => setTimeout(resolve, 2000));

        dispatch(logoutAndDeleteToken());
        navigate('/login', { state: location.state, replace: true });
      } catch (e: any) {
        try {
          const message = (await e.response.json()).message;
          setStatus({ message: message });
        } catch (_) {
          // Need to handle errors as using API with no middleware
          setStatus({ message: 'Technical error, try again later.' });
        }
      } finally {
        setSubmitting(false);
      }
    },
    []
  );

  return {
    submitResetPasswordForm,
  };
}

export function useSubmitForgotPasswordForm() {
  const dispatch = useDispatch();
  const turnstileRef = React.useRef<TurnstileInstance | undefined>(undefined);

  const submitForgotPasswordForm = useCallback(
    async (values: ForgotPasswordFormValues, { setStatus, setSubmitting }: FormikHelpers<ForgotPasswordFormValues>) => {
      try {
        await userApi.forgotPassword({
          resetPasswordData: {
            email: values.username,
            cfTurnstileResponse: turnstileRef.current?.getResponse(),
          },
        });
        dispatch(forgotPasswordSent());
      } catch (e: any) {
        setStatus(await parseErrorMessage(e));
      } finally {
        setSubmitting(false);
      }
    },
    []
  );

  return {
    submitForgotPasswordForm,
    turnstileRef,
  };
}

async function refreshToken(refreshToken: string): Promise<TokenResponse> {
  const formData = new FormData();
  formData.append('grant_type', 'refresh_token');
  formData.append('refresh_token', refreshToken);

  const request: any = {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: CLIENT_AUTHORIZATION,
    },
  };

  return await fetch(`/oauth/token${QUERY_PARAMS_FOR_LOGGING.refresh}`, request)
    .then(convertResponseToJson)
    .then(convertToken);
}

export const refreshAuthentication = async (dispatch: Dispatch, auth: StoredToken): Promise<unknown> => {
  const jwt = auth.refreshToken;
  if (!jwt) {
    dispatch(showFeedback({ message: 'Not logged in' }));
    return dispatch(logoutAndDeleteToken());
  }
  try {
    const newToken = await refreshToken(jwt);
    if (newToken.error || newToken.errorDescription) {
      dispatch(showFeedback({ message: newToken.errorDescription || 'Authentication error' }));
      return dispatch(logoutAndDeleteToken());
    }
    return dispatch(setAuthTokenOnRefresh(newToken));
  } catch (e: unknown) {
    dispatch(showFeedback({ message: await parseErrorMessage(e) }));
    return dispatch(logoutAndDeleteToken());
  }
};

const QUERY_PARAMS_FOR_LOGGING = {
  password: '?grant-type=password',
  mfa: '?grant-type=mfa',
  implicit: '?grant-type=implicit',
  refresh: '?grant-type=refresh',
};
